/**
 * The browser side of tracing: decode a file, rasterize it, hand the pixels to
 * `lib/trace` (which is pure and knows nothing about a DOM), and turn what
 * comes back into the parts payload the API expects.
 *
 * Everything here touches the DOM, so nothing here is unit tested — the
 * arithmetic it depends on lives in `geometry.ts`, which is.
 */

import {
  otsuThreshold,
  pxToMm,
  qualityCheck,
  rgbaToGray,
  traceShapes,
  type QualityFlag,
} from '@app/lib/trace'
import type { TracedPartInput } from '../../../worker/api/saya.contract'
import { scaleRing, traceOptionsFor, traceScaleFor } from './geometry'

export interface DecodedImage {
  source: ImageBitmap
  width: number
  height: number
}

/** Decodes an uploaded scan. Throws a Turkish error the screen can print. */
export async function decodeImageFile(file: Blob): Promise<DecodedImage> {
  try {
    const bitmap = await createImageBitmap(file)
    return { source: bitmap, width: bitmap.width, height: bitmap.height }
  } catch {
    throw new Error('Görsel okunamadı. PNG ya da JPEG bir tarama seçin.')
  }
}

/** Loads a scan already stored in R2 (same-origin, behind the session cookie). */
export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.decoding = 'async'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Tarama görüntüsü yüklenemedi.'))
    image.src = src
  })
}

export interface TraceRun {
  /** One entry per traced shape, in the order the rail lists them. */
  parts: TracedPartInput[]
  /**
   * The trace's checks, in the tracer's own English codes — informational
   * notes, never gates. A flag addresses a shape by index into `parts`, which
   * is why the raw shapes do not travel with it — the index is the only thing
   * the messages need.
   */
  flags: QualityFlag[]
}

/**
 * Traces one scan.
 *
 * The raster handed to the tracer is downsampled (`traceScaleFor`), and the
 * px-per-mm is scaled by the same factor — so millimetres come out identical
 * whatever the scanner's dpi was, while the tracer only ever walks a raster of
 * a size a browser can handle in one frame budget. Contours are lifted back to
 * the uploaded image's own pixels before they leave this function, so `srcPx`
 * on the wire always means "pixels of the file you uploaded".
 */
export function traceScan(
  source: CanvasImageSource,
  width: number,
  height: number,
  pxPerMmX: number,
  pxPerMmY: number,
): TraceRun {
  const scale = traceScaleFor(width, height)
  const rasterWidth = Math.max(1, Math.round(width * scale))
  const rasterHeight = Math.max(1, Math.round(height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = rasterWidth
  canvas.height = rasterHeight
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('Tarayıcı canvas desteği vermedi, iz çıkarılamadı.')
  context.drawImage(source, 0, 0, rasterWidth, rasterHeight)

  const { data } = context.getImageData(0, 0, rasterWidth, rasterHeight)
  const raster = rgbaToGray(rasterWidth, rasterHeight, data)
  const threshold = otsuThreshold(raster)

  // Thresholds belong to the raster we are actually walking, not the file.
  const rasterPxPerMmX = pxPerMmX * scale
  const rasterPxPerMmY = pxPerMmY * scale
  const options = traceOptionsFor(rasterPxPerMmX, rasterPxPerMmY)

  const shapes = traceShapes(raster, threshold, options)
  const flags = qualityCheck(shapes, raster, options.minAreaPx)

  const upscale = scale === 0 ? 1 : 1 / scale
  const parts = shapes.map((shape, index): TracedPartInput => {
    const mm = pxToMm(shape, rasterPxPerMmX, rasterPxPerMmY, rasterHeight)
    return {
      name: `Parça ${index + 1}`,
      // `holes: []` on both geometries: the trace stopped producing hole rings
      // (desi is outer-ring area), and the key stays for the wire shape.
      srcPx: {
        outer: scaleRing(shape.outer, upscale),
        holes: [],
      },
      geomMm: { outer: mm.outer, holes: [] },
      // Inner marks are classified by hand in a later slice; an empty list is
      // honest, an auto-guess would not be.
      features: [],
      netMm2: mm.netMm2,
      perimMm: mm.perimMm,
    }
  })

  return { parts, flags }
}

/**
 * SHA-256 of the uploaded bytes, hex. Sent as an optional header so a scan can
 * be matched to the file it came from later; a browser that refuses (an
 * insecure origin has no `crypto.subtle`) simply does not send one.
 */
export async function sha256Hex(file: Blob): Promise<string | undefined> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return undefined
  }
}
